import React from 'react';
import { ExternalLink, BookOpen, BarChart3, Landmark, Newspaper, Globe, Shield } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';

/**
 * Teacher Toolkit — curated external links social studies teachers reach for daily.
 *
 * Editorial-style tile layout grouped by category. Each tile carries the
 * publisher's brand color as an accent stripe + a high-contrast thumbnail.
 * All thumbnails are sourced from each site's public OG/favicon so they
 * always reflect the live brand.
 */
const SECTIONS = [
  {
    title: 'Archives & Primary Sources',
    icon: BookOpen,
    blurb: 'Foundational repositories every social studies classroom should bookmark.',
    links: [
      {
        name: 'Library of Congress',
        url: 'https://www.loc.gov/classroom-materials/',
        blurb: 'Free classroom materials, primary sources, and lesson plans curated by Library of Congress educators.',
        accent: '#7A2A2A',
        thumb: 'https://www.loc.gov/static/images/og-image.jpg',
      },
      {
        name: 'National Archives — DocsTeach',
        url: 'https://www.docsteach.org/',
        blurb: 'Thousands of primary source documents from the National Archives, plus interactive teaching activities.',
        accent: '#1A3A6E',
        thumb: 'https://www.docsteach.org/images/DocsTeachLogo.png',
      },
      {
        name: 'Smithsonian Learning Lab',
        url: 'https://learninglab.si.edu/',
        blurb: 'Discover, create, and share lessons using the Smithsonian\'s digital museum collections.',
        accent: '#003366',
        thumb: 'https://learninglab.si.edu/static/images/landing/learning-lab-bg.jpg',
      },
    ],
  },
  {
    title: 'Research & Data',
    icon: BarChart3,
    blurb: 'Nonpartisan polling, demographic, and policy data your students can cite.',
    links: [
      {
        name: 'Pew Research Center',
        url: 'https://www.pewresearch.org/',
        blurb: 'Nonpartisan polling, demographic research, and social trend data on U.S. politics, religion, and global attitudes.',
        accent: '#B22234',
        thumb: 'https://www.pewresearch.org/wp-content/themes/pew-research-center/static/img/pew-research-center-logo.png',
      },
      {
        name: 'FiveThirtyEight',
        url: 'https://fivethirtyeight.com/',
        blurb: 'Data-driven journalism on politics, sports, economics, and culture. Great source for charts students can analyze.',
        accent: '#ED713A',
        thumb: 'https://fivethirtyeight.com/wp-content/uploads/2018/05/fivethirtyeight-logo-2-1.png',
      },
      {
        name: 'Census Bureau',
        url: 'https://www.census.gov/library/visualizations.html',
        blurb: 'Official U.S. demographic visualizations and data tools — population, housing, income, and more.',
        accent: '#1F4E79',
        thumb: 'https://www.census.gov/library/visualizations.html/_jcr_content/root/responsivegrid/iparsys/imagecard/image.coreimg.jpeg/1700587987234.jpeg',
      },
    ],
  },
  {
    title: 'Kansas Government',
    icon: Landmark,
    blurb: 'Track legislation in real time and find your students\' elected representatives.',
    links: [
      {
        name: 'KS House of Representatives',
        url: 'https://www.kslegislature.gov/li/b2025_26/chamber/house/roster/',
        blurb: 'Roster, committees, bill tracker, and live floor video from the Kansas House.',
        accent: '#003366',
        thumb: '/about/golden-skies.webp',
      },
      {
        name: 'KS Senate',
        url: 'https://www.kslegislature.gov/li/b2025_26/chamber/senate/roster/',
        blurb: 'Roster, committees, bill tracker, and live floor video from the Kansas Senate.',
        accent: '#7A2A2A',
        thumb: '/about/golden-skies.webp',
      },
      {
        name: 'Kansas Open Records (KORA)',
        url: 'https://ag.ks.gov/about-the-office/divisions/open-government',
        blurb: 'Citizen guide to requesting public records from any Kansas state or local agency.',
        accent: '#5B7DB1',
        thumb: '/about/golden-skies.webp',
      },
      {
        name: 'Find My Representative (KS)',
        url: 'https://www.kslegislature.gov/li/m/findmylegislator/',
        blurb: 'Students enter an address and discover their three state legislators with bios and contact info.',
        accent: '#14213D',
        thumb: '/about/golden-skies.webp',
      },
    ],
  },
  {
    title: 'Center-Aligned News',
    icon: Newspaper,
    blurb: 'AllSides-rated Center or balanced sources for classroom current-events discussions.',
    links: [
      {
        name: 'Reuters',
        url: 'https://www.reuters.com/',
        blurb: 'AllSides rating: Center. Wire-service reporting on global breaking news with minimal editorial framing.',
        accent: '#FF8000',
        thumb: 'https://www.reuters.com/pf/resources/images/reuters/logo-vertical-default-512x512.png?d=181',
      },
      {
        name: 'Associated Press',
        url: 'https://apnews.com/',
        blurb: 'AllSides rating: Center. Wire-service reporting used by both left- and right-leaning outlets.',
        accent: '#E03C31',
        thumb: 'https://apnews.com/_next/image?url=%2Fimages%2Fap-news-logo.png&w=384&q=75',
      },
      {
        name: 'BBC News',
        url: 'https://www.bbc.com/news',
        blurb: 'AllSides rating: Center / Lean-Left. Strong international coverage and explainer journalism.',
        accent: '#BB1919',
        thumb: 'https://nav.files.bbci.co.uk/orbit/8c7472edd9f60cd9c0a4a0fc6440c70bf99dfba1/img/orb-bbcnews-pink.png',
      },
      {
        name: 'AllSides',
        url: 'https://www.allsides.com/unbiased-balanced-news',
        blurb: 'Side-by-side coverage of every story from Left, Center, and Right perspectives. Perfect for media-literacy lessons.',
        accent: '#5B5B5B',
        thumb: 'https://www.allsides.com/sites/default/files/AllSidesLogo-Color-Fullsize.png',
      },
      {
        name: 'Ground News',
        url: 'https://ground.news/',
        blurb: 'Aggregates the same story across hundreds of outlets, color-coded by political lean and factuality score.',
        accent: '#2E5D9F',
        thumb: 'https://ground.news/images/ground-news-logo-300.png',
      },
      {
        name: 'C-SPAN Classroom',
        url: 'https://www.c-span.org/classroom/',
        blurb: 'Unfiltered government footage, lesson plans, and "Bell Ringer" clips designed for teachers.',
        accent: '#003366',
        thumb: 'https://www.c-span.org/assets/img/classroom/cspan-classroom-logo.png',
      },
    ],
  },
  {
    title: 'Civic Engagement',
    icon: Shield,
    blurb: 'Tools that help students participate in democracy beyond the classroom.',
    links: [
      {
        name: 'iCivics',
        url: 'https://www.icivics.org/',
        blurb: 'Free games and lesson plans founded by Justice Sandra Day O\'Connor — government, justice, civics simulations.',
        accent: '#1A3A6E',
        thumb: 'https://www.icivics.org/sites/default/files/icivics-logo.png',
      },
      {
        name: 'Ballotpedia',
        url: 'https://ballotpedia.org/',
        blurb: 'Nonpartisan encyclopedia of every elected official, ballot measure, and election in the U.S.',
        accent: '#0E5E9C',
        thumb: 'https://ballotpedia.s3.amazonaws.com/images/thumb/9/9c/BP_logo_2017_revised.png/640px-BP_logo_2017_revised.png',
      },
      {
        name: 'USA.gov',
        url: 'https://www.usa.gov/',
        blurb: 'The official guide to government services, agencies, and how to contact elected officials.',
        accent: '#003366',
        thumb: 'https://www.usa.gov/themes/usagov/images/usagov-logo.svg',
      },
    ],
  },
  {
    title: 'Global Perspective',
    icon: Globe,
    blurb: 'International data and primary sources for World History units.',
    links: [
      {
        name: 'United Nations',
        url: 'https://www.un.org/en/global-issues',
        blurb: 'Official UN documents, treaties, and global-issue dashboards covering 193 member states.',
        accent: '#5B92E5',
        thumb: 'https://www.un.org/sites/un2.un.org/files/styles/large/public/field/image/un_logo_blue_0.png',
      },
      {
        name: 'World Bank Data',
        url: 'https://data.worldbank.org/',
        blurb: 'Free open data on global development — GDP, education, health, environment for every country.',
        accent: '#002244',
        thumb: 'https://www.worldbank.org/etc.clientlibs/cms-aem-conversion-aem65-renovation/clientlibs/site-clientlib/resources/img/og-image.png',
      },
      {
        name: 'CIA World Factbook',
        url: 'https://www.cia.gov/the-world-factbook/',
        blurb: 'In-depth profiles of every country: geography, government, economy, military, and demographics.',
        accent: '#1A1A2E',
        thumb: 'https://www.cia.gov/the-world-factbook/static/89e8aae35dd6bf5b7cb38de93cef36ed/the-world-factbook-og-image.png',
      },
    ],
  },
];

const handleThumbError = (e) => {
  // If a publisher's logo URL ever breaks, fall back to a transparent pixel
  // so the colored accent stripe + name + blurb still carry the tile.
  e.currentTarget.style.display = 'none';
};

const ToolkitLinkCard = ({ link }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group block"
    data-testid={`toolkit-link-${link.name.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <Card className="border border-[#EBE7E0] hover:border-[#14213D]/30 hover:shadow-md transition-all overflow-hidden bg-white h-full flex flex-col">
      <div
        className="h-1.5 w-full"
        style={{ background: link.accent }}
      />
      <div className="aspect-[16/9] bg-[#F9F8F4] flex items-center justify-center p-6 overflow-hidden">
        <img
          src={link.thumb}
          alt={`${link.name} logo`}
          className="max-w-full max-h-full object-contain group-hover:scale-[1.03] transition-transform"
          onError={handleThumbError}
        />
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className="font-serif-display text-base text-[#14213D] leading-tight">{link.name}</h3>
          <ExternalLink className="h-3.5 w-3.5 text-[#A0AEC0] group-hover:text-[#D4AF37] transition-colors flex-shrink-0 mt-1" />
        </div>
        <p className="text-xs text-[#5A6577] leading-relaxed">{link.blurb}</p>
      </div>
    </Card>
  </a>
);

export default function TeacherToolkitPage() {
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="teacher-toolkit-page">
        <div className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight">Teacher Toolkit</h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">
            Curated external sites social studies teachers use daily &mdash; archives, research,
            Kansas government, balanced news, civic-engagement tools, and global data. Click any tile
            to open in a new tab.
          </p>
        </div>

        {SECTIONS.map((section) => {
          const SectionIcon = section.icon;
          return (
            <section
              key={section.title}
              className="mb-12"
              data-testid={`toolkit-section-${section.title.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <SectionIcon className="h-4 w-4 text-[#D4AF37]" />
                <h2 className="font-serif-display text-xl text-[#14213D]">{section.title}</h2>
              </div>
              <p className="text-xs text-[#5A6577] mb-5 ml-6">{section.blurb}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {section.links.map((link) => (
                  <ToolkitLinkCard key={link.url} link={link} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </AppShell>
  );
}
